import { Link } from "react-router-dom";
import { ArrowRight, Globe2, FlaskConical, Mountain, HeartHandshake } from "lucide-react";
import Seo from "../components/Seo";
import { IMAGES } from "../siteConfig";
import { Reveal, SectionHead } from "../components/Reveal";

const BKT_STRENGTHS = [
  {
    icon: Globe2,
    title: "A Global Indian Brand",
    copy: "BKT — Balkrishna Industries Ltd. — is a homegrown Indian multinational, founded in 1987 and headquartered in Mumbai, whose tyres run in more than 160 countries worldwide.",
  },
  {
    icon: Mountain,
    title: "Off-Highway Specialists",
    copy: "Unlike generalist tyre makers, BKT focuses entirely on off-highway and specialty tyres — agriculture, construction, industrial, mining and commercial vehicles. Every pattern is engineered for a specific job.",
  },
  {
    icon: FlaskConical,
    title: "Engineering-Led",
    copy: "BKT designs and builds its own moulds, compounds and machinery, with in-house R&D centres and state-of-the-art plants across India — a rare depth of control over quality.",
  },
  {
    icon: HeartHandshake,
    title: "Growing Together",
    copy: "BKT's philosophy is partnership — with dealers, fleets and industries. That is exactly how we work at Mangalam Group, which is why this association fits so naturally.",
  },
];

const SEGMENTS = [
  {
    title: "OTR & Earthmoving",
    company: "Apex Infra",
    copy: "BKT off-highway tyres for loaders, graders, excavators, dumpers and heavy mining equipment.",
    image: IMAGES.loader,
  },
  {
    title: "Commercial Vehicle Radial",
    company: "Mangalam Enterprises",
    copy: "BKT CVR tyres for trucks and buses — the Loadexpert and Milexpert range built for Indian roads and fleet mileage.",
    image: "/assets/products/loadexpert-a.png",
  },
  {
    title: "Industrial",
    company: "Mangalam Enterprises",
    copy: "BKT industrial tyres for forklifts, cranes, compactors, port equipment and material handling.",
    image: IMAGES.tread,
  },
];

export default function Bkt() {
  return (
    <div data-testid="bkt-page" className="pt-[72px]">
      <Seo
        title="BKT Tyre Solutions in Jharkhand | Mangalam Group"
        description="Mangalam Group distributes BKT OTR, earthmoving, industrial and commercial vehicle radial tyres across its territories and customer segments."
        path="/bkt"
      />

      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 tech-grid opacity-50" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-ember">Strategic Partnership</p>
            <h1 className="mt-5 font-condensed text-5xl sm:text-6xl lg:text-7xl font-bold uppercase leading-[0.98] tracking-tight text-white">
              Powered by <span className="text-ember">BKT</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-300">
              Behind every tyre we supply stands one of the world's most respected
              off-highway tyre manufacturers.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-start">
            <SectionHead
              eyebrow="Who is BKT"
              title="A Name the Industry Trusts"
              copy="BKT — Balkrishna Industries Ltd. — began in 1987 in India and has grown into one of the world's leading off-highway tyre manufacturers. From farms in Europe to mines in Australia and construction sites across India, BKT tyres are chosen where failure is not an option. The company is known for its remarkable range — thousands of SKUs built for specific machines and conditions — and for a quality standard that has made an Indian brand a global benchmark in specialty tyres."
            />
            <Reveal delay={0.12}>
              <div className="overflow-hidden border border-white/10">
                <img src={IMAGES.machinery} alt="BKT off-highway tyres on heavy mining machinery" className="h-72 w-full object-cover img-industrial" />
              </div>
            </Reveal>
          </div>

          <div className="mt-16 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {BKT_STRENGTHS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.07} className="bg-obsidian">
                <div className="h-full p-7">
                  <s.icon className="h-6 w-6 text-ember" strokeWidth={1.5} />
                  <h3 className="mt-5 font-condensed text-xl font-bold uppercase tracking-wide text-white">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-obsidian-2 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            eyebrow="The Partnership"
            title="What This Means for You"
            copy="Mangalam Group companies are associated with and distribute BKT products across their relevant territories and segments. For our customers, that means genuine BKT products, correct application guidance, dependable availability and service that stays with you after the sale. Mangalam Group is an independent group — BKT is our tyre technology partner, and we are proud to represent their range."
          />

          <div className="mt-16 grid gap-6 md:grid-cols-3">
            {SEGMENTS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="group h-full border border-white/10 bg-obsidian">
                  <div className="h-44 overflow-hidden bg-obsidian-3">
                    <img src={s.image} alt={`BKT ${s.title} tyres`} className="h-full w-full object-contain p-4 img-industrial transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-ember">{s.company}</p>
                    <h3 className="mt-2 font-condensed text-xl font-bold uppercase tracking-wide text-white">{s.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.copy}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-14">
            <Link
              to="/products"
              data-testid="bkt-explore-cta"
              className="inline-flex items-center gap-2 bg-ember px-7 py-4 font-mono text-[11px] tracking-[0.22em] text-[#10141B] transition-colors hover:bg-ember-hover"
            >
              EXPLORE BKT TYRE SOLUTIONS <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <p className="mt-8 font-mono text-[10px] tracking-[0.15em] uppercase text-slate-300">
            BKT is a trademark of Balkrishna Industries Ltd.
          </p>
        </div>
      </section>
    </div>
  );
}
